'use client';

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'next/navigation';
import { MessageSquare, ChevronDown, ChevronUp, Send, X, Plus, Loader2, CreditCard } from 'lucide-react';
import { toast } from 'sonner';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

interface MessageReply {
  id: string;
  body: string;
  from_admin: boolean;
  created_at: string;
  stripe_payment_link?: string | null;
}

interface UserMessage {
  id: string;
  subject: string;
  body: string;
  kind: string;
  read_by_recipient: boolean;
  created_at: string;
  replies: MessageReply[];
}

function formatTs(ts: string) {
  return new Date(ts).toLocaleDateString('de-DE', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default function UserInbox() {
  const { user } = useAuth();
  const supabase = createClient();
  const searchParams = useSearchParams();

  const [messages, setMessages] = useState<UserMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [replyText, setReplyText] = useState<Record<string, string>>({});
  const [sendingReply, setSendingReply] = useState<string | null>(null);
  const [showNewMessage, setShowNewMessage] = useState(false);
  const [newMsg, setNewMsg] = useState({ subject: '', body: '' });
  const [sendingNew, setSendingNew] = useState(false);
  const [liveConnected, setLiveConnected] = useState(false);
  const threadEndRef = useRef<HTMLDivElement | null>(null);
  const deepLinked = useRef(false);

  // Clear app badge when user opens the inbox (they are reading messages)
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({ type: 'SET_BADGE', count: 0 });
    }
    if ('clearAppBadge' in navigator) {
      (navigator as any).clearAppBadge().catch(() => {});
    }
  }, []);

  const loadMessages = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const { data: msgData, error } = await supabase
        .from('messages')
        .select('id, subject, body, kind, read_by_recipient, created_at')
        .eq('sender_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      const messagesWithReplies: UserMessage[] = await Promise.all(
        (msgData || []).map(async (msg: any) => {
          const { data: replies } = await supabase
            .from('message_replies')
            .select('id, body, from_admin, created_at, stripe_payment_link')
            .eq('message_id', msg.id)
            .order('created_at', { ascending: true });
          return { ...msg, replies: replies || [] };
        })
      );

      setMessages(messagesWithReplies);
    } catch (err: any) {
      toast.error('Fehler beim Laden der Nachrichten');
    } finally {
      setLoading(false);
    }
  }, [user, supabase]);

  useEffect(() => {
    loadMessages();
  }, [loadMessages]);

  // Handle deep-link from push notification: ?tab=messages&message=<id>
  useEffect(() => {
    if (loading || deepLinked.current) return;
    const tabParam = searchParams.get('tab');
    const msgParam = searchParams.get('message');
    if (tabParam === 'messages') {
      deepLinked.current = true;
      if (msgParam) {
        setExpandedId(msgParam);
        const msg = messages.find((m) => m.id === msgParam);
        if (msg && !msg.read_by_recipient) {
          supabase.from('messages').update({ read_by_recipient: true }).eq('id', msgParam);
          setMessages((prev) =>
            prev.map((m) => (m.id === msgParam ? { ...m, read_by_recipient: true } : m))
          );
        }
        // Scroll to the specific message after it has expanded
        setTimeout(() => {
          const el = document.getElementById(`msg-${msgParam}`);
          if (el) {
            const rect = el.getBoundingClientRect();
            const scrollTop = window.scrollY + rect.top - 80;
            window.scrollTo({ top: scrollTop, behavior: 'smooth' });
          }
        }, 400);
      }
    }
  }, [loading, messages, searchParams]);

  // Scroll to bottom of thread when expanded
  useEffect(() => {
    if (expandedId) {
      setTimeout(() => {
        threadEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 150);
    }
  }, [expandedId]);

  // Realtime subscriptions
  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel(`user-inbox-${user.id}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'message_replies',
        },
        async (payload) => {
          const newReply = payload.new as MessageReply & { message_id: string };
          if (newReply.from_admin) {
            setMessages((prev) =>
              prev.map((m) =>
                m.id === newReply.message_id
                  ? {
                      ...m,
                      replies: m.replies.some((r) => r.id === newReply.id)
                        ? m.replies
                        : [
                            ...m.replies,
                            {
                              id: newReply.id,
                              body: newReply.body,
                              from_admin: true,
                              created_at: newReply.created_at,
                            },
                          ],
                    }
                  : m
              )
            );
          }
        }
      )
      .subscribe((status) => {
        setLiveConnected(status === 'SUBSCRIBED');
      });

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user]);

  const unreadMessages = messages.filter((m) => !m.read_by_recipient).length;

  const handleExpand = async (id: string) => {
    setExpandedId(expandedId === id ? null : id);
    const msg = messages.find((m) => m.id === id);
    if (msg && !msg.read_by_recipient) {
      await supabase.from('messages').update({ read_by_recipient: true }).eq('id', id);
      setMessages((prev) =>
        prev.map((m) => (m.id === id ? { ...m, read_by_recipient: true } : m))
      );
    }
  };

  const handleSendReply = async (msgId: string) => {
    const text = replyText[msgId]?.trim();
    if (!text || !user) return;
    setSendingReply(msgId);
    try {
      const { data, error } = await supabase
        .from('message_replies')
        .insert({ message_id: msgId, sender_id: user.id, body: text, from_admin: false })
        .select('id, body, from_admin, created_at')
        .single();
      if (error) throw error;
      setMessages((prev) =>
        prev.map((m) => (m.id === msgId ? { ...m, replies: [...m.replies, data] } : m))
      );
      setReplyText((prev) => ({ ...prev, [msgId]: '' }));
      toast.success('Nachricht gesendet', { icon: '📨' });

      // Notify admins about the user reply
      const msg = messages.find((m) => m.id === msgId);
      fetch('/api/push/send-admins', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: '💬 Neue Antwort eines Nutzers',
          body: `${msg?.subject ? `"${msg.subject}" — ` : ''}${text.slice(0, 80)}${text.length > 80 ? '…' : ''}`,
          url: '/admin/messages',
          topic: 'admin-message',
        }),
      }).catch(() => {});
    } catch (err: any) {
      toast.error(err?.message || 'Fehler beim Senden');
    } finally {
      setSendingReply(null);
    }
  };

  const handleSendNew = async () => {
    if (!newMsg.subject.trim() || !newMsg.body.trim()) {
      toast.error('Betreff und Nachricht sind erforderlich');
      return;
    }
    if (!user) return;
    setSendingNew(true);
    try {
      const { data: adminProfiles } = await supabase
        .from('profiles')
        .select('id')
        .eq('role', 'admin')
        .limit(1);

      const recipientId = adminProfiles?.[0]?.id || null;

      const { data, error } = await supabase
        .from('messages')
        .insert({
          sender_id: user.id,
          recipient_id: recipientId,
          subject: newMsg.subject.trim(),
          body: newMsg.body.trim(),
          kind: 'contact',
          status: 'unread',
          read_by_recipient: true,
        })
        .select('id, subject, body, kind, read_by_recipient, created_at')
        .single();

      if (error) throw error;

      fetch('/api/push/send-admins', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: '📬 Neue Nachricht an Winewood',
          body: `${newMsg.subject.trim()} — ${newMsg.body.trim().slice(0, 80)}${newMsg.body.trim().length > 80 ? '…' : ''}`,
          url: '/admin/messages',
          topic: 'admin-message',
        }),
      }).catch(() => {});

      setMessages((prev) => [{ ...data, replies: [] }, ...prev]);
      setNewMsg({ subject: '', body: '' });
      setShowNewMessage(false);
      toast.success('Nachricht gesendet!', { icon: '✉️' });
    } catch (err: any) {
      toast.error(err?.message || 'Fehler beim Senden');
    } finally {
      setSendingNew(false);
    }
  };

  return (
    <div
      className="rounded-app overflow-hidden"
      style={{ background: 'var(--card)', border: '1px solid var(--border)' }}
    >
      {/* Header */}
      <div
        className="flex items-center justify-between px-4 py-3"
        style={{ borderBottom: '1px solid var(--border)' }}
      >
        <div className="flex items-center gap-2">
          <MessageSquare size={16} color="var(--primary)" />
          <span className="text-sm font-bold" style={{ color: 'var(--card-foreground)' }}>
            Nachrichten
          </span>
          {unreadMessages > 0 && (
            <span
              className="text-[10px] font-bold px-1.5 py-0.5 rounded-pill"
              style={{ background: 'rgba(239,68,68,0.15)', color: '#ef4444' }}
            >
              {unreadMessages} neu
            </span>
          )}
          {/* Live indicator */}
          <div
            className="flex items-center gap-1 px-1.5 py-0.5 rounded-pill"
            style={{
              background: liveConnected ? 'rgba(124,179,66,0.1)' : 'rgba(100,100,100,0.1)',
            }}
          >
            <div
              className="w-1.5 h-1.5 rounded-full"
              style={{ background: liveConnected ? 'var(--primary)' : 'var(--muted-foreground)' }}
            />
            <span
              className="text-[9px] font-semibold"
              style={{
                color: liveConnected ? 'var(--primary)' : 'var(--muted-foreground)',
              }}
            >
              Live
            </span>
          </div>
        </div>
        <button
          onClick={() => setShowNewMessage(!showNewMessage)}
          className="flex items-center gap-1 px-2.5 py-1.5 rounded-pill text-[11px] font-semibold transition-all duration-150 active:scale-95"
          style={{ background: 'var(--primary)', color: '#fff' }}
        >
          {showNewMessage ? <X size={11} /> : <Plus size={11} />}
          {showNewMessage ? 'Abbrechen' : 'Neu'}
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 size={20} className="animate-spin" color="var(--primary)" />
        </div>
      ) : (
        <>
          {/* New message form */}
          {showNewMessage && (
            <div className="p-4 space-y-3" style={{ borderBottom: '1px solid var(--border)' }}>
              <div>
                <label
                  className="text-xs font-semibold mb-1 block"
                  style={{ color: 'var(--muted-foreground)' }}
                >
                  Betreff
                </label>
                <input
                  value={newMsg.subject}
                  onChange={(e) => setNewMsg((p) => ({ ...p, subject: e.target.value }))}
                  placeholder="Worum geht es?"
                  className="w-full px-3 py-2 rounded-app-sm text-sm"
                  style={{
                    background: 'var(--input)',
                    border: '1px solid var(--border)',
                    color: 'var(--card-foreground)',
                  }}
                />
              </div>
              <div>
                <label
                  className="text-xs font-semibold mb-1 block"
                  style={{ color: 'var(--muted-foreground)' }}
                >
                  Nachricht
                </label>
                <textarea
                  value={newMsg.body}
                  onChange={(e) => setNewMsg((p) => ({ ...p, body: e.target.value }))}
                  placeholder="Deine Nachricht an WINEWOOD..."
                  rows={3}
                  className="w-full px-3 py-2 rounded-app-sm text-sm resize-none"
                  style={{
                    background: 'var(--input)',
                    border: '1px solid var(--border)',
                    color: 'var(--card-foreground)',
                  }}
                />
              </div>
              <button
                onClick={handleSendNew}
                disabled={sendingNew}
                className="w-full py-2.5 rounded-app-sm text-sm font-semibold text-white transition-all duration-150 active:scale-95 flex items-center justify-center gap-2"
                style={{ background: 'var(--primary)', opacity: sendingNew ? 0.7 : 1 }}
              >
                {sendingNew ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                Senden
              </button>
            </div>
          )}

          {/* Message list */}
          {messages.length === 0 ? (
            <div className="px-4 py-8 text-center">
              <MessageSquare size={28} color="var(--muted-foreground)" className="mx-auto mb-2" />
              <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>
                Noch keine Nachrichten
              </p>
              <button
                onClick={() => setShowNewMessage(true)}
                className="mt-3 text-xs font-semibold"
                style={{ color: 'var(--primary)' }}
              >
                Erste Nachricht schreiben →
              </button>
            </div>
          ) : (
            <div>
              {messages.map((msg, i) => {
                const isUnread = !msg.read_by_recipient;
                const hasNewAdminReply =
                  isUnread &&
                  msg.replies.length > 0 &&
                  msg.replies[msg.replies.length - 1].from_admin;

                return (
                  <div
                    key={msg.id}
                    id={`msg-${msg.id}`}
                    style={{
                      borderBottom: i < messages.length - 1 ? '1px solid var(--border)' : 'none',
                      borderLeft: isUnread ? '3px solid var(--primary)' : '3px solid transparent',
                      transition: 'border-color 0.2s',
                    }}
                  >
                    <button
                      className="w-full px-4 py-3 text-left transition-colors duration-150"
                      style={{
                        background: isUnread
                          ? 'rgba(110,168,50,0.07)'
                          : expandedId === msg.id
                          ? 'rgba(124,179,66,0.04)'
                          : 'transparent',
                      }}
                      onClick={() => handleExpand(msg.id)}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            {isUnread && (
                              <span className="relative flex h-2.5 w-2.5 flex-shrink-0">
                                <span
                                  className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75"
                                  style={{ background: 'var(--primary)' }}
                                />
                                <span
                                  className="relative inline-flex rounded-full h-2.5 w-2.5"
                                  style={{ background: 'var(--primary)' }}
                                />
                              </span>
                            )}
                            <span
                              className="text-sm truncate"
                              style={{
                                color: 'var(--card-foreground)',
                                fontWeight: isUnread ? 700 : 500,
                              }}
                            >
                              {msg.subject}
                            </span>
                            {hasNewAdminReply && (
                              <span
                                className="text-[10px] font-bold px-2 py-0.5 rounded-pill flex-shrink-0"
                                style={{
                                  background: 'var(--primary)',
                                  color: '#fff',
                                  letterSpacing: '0.3px',
                                }}
                              >
                                Neue Antwort ↓
                              </span>
                            )}
                            {isUnread && !hasNewAdminReply && (
                              <span
                                className="text-[10px] font-bold px-2 py-0.5 rounded-pill flex-shrink-0"
                                style={{
                                  background: 'rgba(110,168,50,0.15)',
                                  color: 'var(--primary)',
                                  border: '1px solid rgba(110,168,50,0.3)',
                                }}
                              >
                                Neu
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span
                              className="text-[10px] px-1.5 py-0.5 rounded-pill"
                              style={{
                                background: 'var(--muted)',
                                color: 'var(--muted-foreground)',
                              }}
                            >
                              {msg.kind === 'inquiry' ? 'Anfrage' : 'Kontakt'}
                            </span>
                            <span
                              className="text-[11px]"
                              style={{ color: 'var(--muted-foreground)' }}
                            >
                              {new Date(msg.created_at).toLocaleDateString('de-DE')}
                            </span>
                            {msg.replies.length > 0 && (
                              <span
                                className="text-[11px]"
                                style={{ color: 'var(--primary)' }}
                              >
                                {msg.replies.length} Antwort
                                {msg.replies.length !== 1 ? 'en' : ''}
                              </span>
                            )}
                          </div>
                        </div>
                        {expandedId === msg.id ? (
                          <ChevronUp
                            size={14}
                            color="var(--muted-foreground)"
                            className="flex-shrink-0 mt-1"
                          />
                        ) : (
                          <ChevronDown
                            size={isUnread ? 16 : 14}
                            color={isUnread ? 'var(--primary)' : 'var(--muted-foreground)'}
                            className="flex-shrink-0 mt-1"
                          />
                        )}
                      </div>
                    </button>

                    {expandedId === msg.id && (
                      <div className="px-4 pb-4 space-y-2" style={{ borderTop: '1px solid var(--border)' }}>
                        <p className="text-[10px] font-semibold pt-3 pb-1" style={{ color: 'var(--muted-foreground)' }}>
                          Gesprächsverlauf
                        </p>

                        {/* Original message bubble */}
                        <div
                          className="p-3 rounded-app-sm"
                          style={{ background: 'var(--muted)', border: '1px solid var(--border)' }}
                        >
                          <div className="flex items-center justify-between mb-1.5">
                            <p
                              className="text-[10px] font-bold"
                              style={{ color: 'var(--muted-foreground)' }}
                            >
                              Du
                            </p>
                            <p className="text-[10px]" style={{ color: 'var(--muted-foreground)' }}>
                              {formatTs(msg.created_at)}
                            </p>
                          </div>
                          <p
                            className="text-xs leading-relaxed"
                            style={{ color: 'var(--card-foreground)' }}
                          >
                            {msg.body}
                          </p>
                        </div>

                        {/* Reply bubbles */}
                        {msg.replies.map((reply) => (
                          <div
                            key={reply.id}
                            className={`p-3 rounded-app-sm ${reply.from_admin ? '' : 'ml-3'}`}
                            style={{
                              background: reply.from_admin
                                ? 'rgba(124,179,66,0.08)'
                                : 'var(--muted)',
                              border: reply.from_admin
                                ? '1px solid rgba(124,179,66,0.2)'
                                : '1px solid var(--border)',
                            }}
                          >
                            <div className="flex items-center justify-between mb-1.5">
                              <p
                                className="text-[10px] font-bold"
                                style={{
                                  color: reply.from_admin
                                    ? 'var(--primary)'
                                    : 'var(--muted-foreground)',
                                }}
                              >
                                {reply.from_admin ? '🌿 WINEWOOD Team' : 'Du'}
                              </p>
                              <p
                                className="text-[10px]"
                                style={{ color: 'var(--muted-foreground)' }}
                              >
                                {formatTs(reply.created_at)}
                              </p>
                            </div>
                            <p
                              className="text-xs leading-relaxed"
                              style={{ color: 'var(--card-foreground)' }}
                            >
                              {reply.body}
                            </p>
                            {reply.stripe_payment_link && (
                              <a
                                href={reply.stripe_payment_link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="mt-2 flex items-center gap-2 px-3 py-2 rounded-app-sm text-xs font-semibold transition-all duration-150 active:scale-95"
                                style={{
                                  background: 'rgba(124,179,66,0.15)',
                                  border: '1px solid rgba(124,179,66,0.35)',
                                  color: 'var(--primary)',
                                  display: 'flex',
                                }}
                              >
                                <CreditCard size={13} />
                                Jetzt bezahlen →
                              </a>
                            )}
                          </div>
                        ))}

                        {/* Scroll anchor */}
                        <div ref={threadEndRef} />

                        {/* Reply input */}
                        <div className="flex gap-2 pt-1">
                          <input
                            value={replyText[msg.id] || ''}
                            onChange={(e) =>
                              setReplyText((p) => ({ ...p, [msg.id]: e.target.value }))
                            }
                            placeholder="Antworten..."
                            className="flex-1 px-3 py-2 rounded-app-sm text-sm"
                            style={{
                              background: 'var(--input)',
                              border: '1px solid var(--border)',
                              color: 'var(--card-foreground)',
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleSendReply(msg.id);
                            }}
                          />
                          <button
                            onClick={() => handleSendReply(msg.id)}
                            disabled={sendingReply === msg.id}
                            className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-150 active:scale-90"
                            style={{ background: 'var(--primary)' }}
                          >
                            {sendingReply === msg.id ? (
                              <Loader2 size={14} color="#fff" className="animate-spin" />
                            ) : (
                              <Send size={14} color="#fff" />
                            )}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}
